import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { PreTransformSchemaFormValues } from '@/zod/schemas/dataSource/PreTransform';
import {
  EmbedColumnOptions,
  PreserveUnreferencedColumnsSchema,
  PreserveUnreferencedColumnsSchemaValues,
} from '@/zod/schemas/dataSource/Common';
import { getFormikNames } from '@/utils/util';
import { FormInput } from '@/components/form/input/FormInput';
import { FormDropdown, FormError } from '@/components/form/form';
import { FormValues } from '@/components/form/values/FormValues';
import {
  columnsToOmitTooltip,
  unreferencedEmbedTooltip,
} from '@/strings/StringsTooltips';

export type UnreferencedFieldsProps = {
  unreferencedFields: PreserveUnreferencedColumnsSchemaValues;
  prefix?: string;
  disabled?: boolean;
};

type PreserveKeys = keyof typeof PreserveUnreferencedColumnsSchema.shape;

export const UnreferencedFields = ({
  unreferencedFields,
  prefix = '',
  disabled = false,
}: UnreferencedFieldsProps) => {
  const formik = useFormikContext<PreTransformSchemaFormValues>();

  const { setFieldValue, handleChange, handleBlur, isSubmitting } = formik;

  const names = useMemo(
    () =>
      getFormikNames<PreserveKeys>(
        PreserveUnreferencedColumnsSchema.shape,
        prefix,
      ),
    [prefix],
  );

  return (
    <>
      <Label>Preserve unreferenced columns</Label>
      <ButtonGroup
        data-testid={`${prefix}preserve-unreferenced-fields-checkbox`}
        options={[
          { label: 'No', value: 'no' },
          { label: 'Yes', value: 'yes' },
        ]}
        value={unreferencedFields.preserve ? 'yes' : 'no'}
        onChange={(value) => {
          setFieldValue(names.preserve, value === 'yes', true);
        }}
        disabled={disabled}
        id={`${prefix}-unreferenced-fields-preserve`}
      />

      {unreferencedFields.preserve && (
        <>
          <Spacer size="lg" />

          <Label className="m-0" info={columnsToOmitTooltip}>
            Columns to omit
          </Label>
          <FormValues
            data-testid={`${prefix}unreferenced-fields-form-values`}
            name={names.omitColumns}
            values={unreferencedFields.omitColumns}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={isSubmitting || disabled}
          />

          <Spacer size="lg" />

          <Label info={unreferencedEmbedTooltip}>
            Embed the unreferenced columns into a new column
          </Label>
          <ButtonGroup
            options={[
              { label: 'No', value: 'no' },
              { label: 'Yes', value: 'yes' },
            ]}
            value={unreferencedFields.embed ? 'yes' : 'no'}
            onChange={(value) =>
              setFieldValue(names.embed, value === 'yes', true)
            }
            disabled={disabled}
            id={`${prefix}unreferenced-fields-embed`}
          />

          {!unreferencedFields.embed && (
            <>
              <Spacer size="lg" />
              <Label
                htmlFor={names.duplicatePrefix}
                id={`${names.duplicatePrefix}-label`}
              >
                Prefix for colliding column names
              </Label>
              <FormInput
                name={names.duplicatePrefix}
                value={unreferencedFields.duplicatePrefix ?? undefined}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Column name prefix"
                disabled={isSubmitting || disabled}
                aria-labelledby={`${names.duplicatePrefix}-label`}
              />
              <FormError name={names.duplicatePrefix} />
            </>
          )}

          {unreferencedFields.embed && (
            <>
              <Spacer size="lg" />

              <Label htmlFor={names.embedColumnType} required>
                Embed column type
              </Label>
              <FormDropdown
                name={names.embedColumnType}
                options={[...EmbedColumnOptions]}
                value={unreferencedFields.embedColumnType}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting || disabled}
              />
              <FormError name={names.embedColumnType} />
              <Spacer size="lg" />

              <Label
                htmlFor={names.embedColumn}
                id={`${names.embedColumn}-label`}
                required
              >
                Column to embed
              </Label>
              <FormInput
                name={names.embedColumn}
                value={unreferencedFields.embedColumn ?? undefined}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Column name"
                disabled={isSubmitting || disabled}
                aria-labelledby={`${names.embedColumn}-label`}
              />
              <FormError name={names.embedColumn} />
            </>
          )}
        </>
      )}
    </>
  );
};
